import { useT } from '@/shared/i18n';
import { Icon } from '@/shared/ui/icon';
import type { MessageKey } from '@/shared/i18n/locales/en';
import type { ReactNode } from 'react';

/**
 * FaqSection — the landing disclosure list (extraction §17/§23c). Native
 * <details>/<summary>, chevron rotates on open, Lime ring on open. `count`
 * controls how many items render (default 6). Theme-adaptive via tokens.
 */
export function FaqSection({ count = 6 }: { count?: 3 | 6 }): ReactNode {
  const { t } = useT();
  const items = Array.from({ length: count }, (_, i) => ({
    q: t(`landing.faq.q${i + 1}` as MessageKey),
    a: t(`landing.faq.a${i + 1}` as MessageKey),
  }));
  return (
    <section id="faq" className="scroll-mt-24 bg-surface-base py-32">
      <div className="mx-auto max-w-[860px] px-margin-mobile md:px-margin-desktop">
        <h2 className="mb-16 text-center text-4xl font-bold tracking-tight text-on-surface md:text-5xl">
          {t('landing.faq.heading')}
        </h2>
        <div className="space-y-4">
          {items.map((item) => (
            <details
              key={item.q}
              className="group rounded-[24px] border border-outline-variant/20 bg-surface-container p-6 transition-all open:border-secondary-fixed/50 md:p-8"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-start text-lg font-semibold text-on-surface focus-visible:outline-none">
                {item.q}
                <Icon
                  name="chevron-down"
                  size={20}
                  className="shrink-0 text-[var(--ink-accent)] transition-transform duration-base ease-standard group-open:rotate-180"
                />
              </summary>
              <p className="mt-4 leading-relaxed text-on-surface-variant">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FaqSection;
